import React, { useState, useEffect } from 'react';
import { Title, Text, Group, Stack as MantineStack, Button, TextInput, Image, Box, Avatar } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Game, Player, Stack, EntryType } from '../types/models';
import { apiClient } from '../services/api';
import { GameLayout } from './layouts/GameLayout';
import { useResponsiveLayout } from '../hooks/useResponsiveLayout';
import { useDraftPersistence } from '../hooks/useDraftPersistence';
import { useSquareCanvas } from '../hooks/useSquareCanvas';

interface GuessDrawingProps {
  game: Game;
  currentPlayer: Player;
  stack: Stack;
  drawingUrl: string;
  entryNumber: number;
  turnsRemaining: number;
  onGuessSubmitted: () => void;
}

const GuessDrawing: React.FC<GuessDrawingProps> = ({
  game,
  currentPlayer,
  stack,
  drawingUrl,
  entryNumber,
  turnsRemaining,
  onGuessSubmitted
}) => {
  const { isMobile } = useResponsiveLayout();
  const { width: imageSize } = useSquareCanvas({
    padding: isMobile ? 8 : 16,
    reservedTop: isMobile ? 180 : 140,
    reservedBottom: isMobile ? 220 : 200,
    maxSize: isMobile ? 600 : 800
  });
  
  // Helper to get the origin player (whose stack this started as)
  const originPlayer = game.players.find(p => p.id === stack.originPlayerId);
  
  // Get the player who created the most recent entry (the drawing we're guessing)
  const mostRecentEntry = stack.entries[stack.entries.length - 1];
  const drawingAuthor = mostRecentEntry ? game.players.find(p => p.id === mostRecentEntry.playerId) : null;
  
  // Helper to render progress dots
  const renderProgressDots = (): string => {
    const totalEntries = game.totalEntriesPerStack || stack.expectedEntries.length;
    const completed = '●'.repeat(entryNumber - 1);
    const remaining = '○'.repeat(totalEntries - entryNumber + 1);
    return completed + remaining;
  };
  const [guess, setGuess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  
  // Get user's handwriting font
  const handwritingFont = localStorage.getItem('userHandwriting') || 'Caveat';
  const { loadDraft, saveDraft, clearDraft } = useDraftPersistence({
    gameKey: game.key,
    stackId: stack.id,
    entryType: 'guess',
    entryNumber
  });

  useEffect(() => {
    let isActive = true;

    const restoreDraft = async () => {
      const saved = await loadDraft();
      if (!isActive || !saved) return;
      setGuess(saved);
    };

    restoreDraft();

    return () => {
      isActive = false;
    };
  }, [loadDraft]);

  useEffect(() => {
    const handle = setTimeout(() => {
      if (hasSubmitted) return;
      if (guess.trim()) {
        saveDraft(guess);
      } else {
        clearDraft();
      }
    }, 300);

    return () => clearTimeout(handle);
  }, [clearDraft, guess, hasSubmitted, saveDraft]);

  // Add navigation guard to prevent accidental back button
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (guess.trim() && !hasSubmitted) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [guess, hasSubmitted]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!guess.trim() || hasSubmitted) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await apiClient.createStackEntry(game.key, stack.id, {
        content: guess.trim(),
        type: 'text'
      });

      setHasSubmitted(true);
      await clearDraft();
      setGuess('');
      onGuessSubmitted();
    } catch (err: any) {
      console.error('Failed to submit guess:', err);
      setError(typeof err === 'string' ? err : err.message || 'Failed to submit guess. Please try again.');
      notifications.show({
        title: 'Failed to submit guess',
        message: 'Unable to save your guess. Please try again.',
        color: 'red',
      });
      setIsSubmitting(false);
    }
  };

  // For mobile, header is minimal
  const header = isMobile ? (
    <Group gap="xs" justify="center">
      <Text size="md" fw={500}>
        {renderProgressDots()}
      </Text>
      <Text size="sm" c="dimmed">
        {entryNumber} of {game.totalEntriesPerStack || stack.expectedEntries.length}
      </Text>
    </Group>
  ) : (
    <MantineStack gap="xs" align="center">
      {/* Progress Dots */}
      <Group gap="xs" justify="center">
        <Text size="lg" fw={500}>
          {renderProgressDots()}
        </Text>
        <Text size="sm" c="dimmed">
          {entryNumber} of {game.totalEntriesPerStack || stack.expectedEntries.length}
        </Text>
      </Group>

      {/* Context Header */}
      <Title order={2} ta="center">What the heck is this?</Title>
      <Group gap="xs" justify="center">
        {drawingAuthor?.avatarUrl && (
          <Avatar 
            src={drawingAuthor.avatarUrl} 
            size="sm" 
            radius="sm"
          />
        )}
        <Text size="md" c="dimmed" ta="center">
          Here's what {drawingAuthor?.name || 'someone'} drew for {originPlayer?.name || 'someone'}'s stack
        </Text>
      </Group>
    </MantineStack>
  );

  // Main content (drawing display)
  const content = (
    <MantineStack gap="lg" align="center" style={{ width: '100%' }}>
      {/* Mobile shows title here instead of header */}
      {isMobile && (
        <MantineStack gap="xs" align="center">
          <Title order={3} ta="center">What the heck is this?</Title>
          <Group gap="xs" justify="center">
            {drawingAuthor?.avatarUrl && (
              <Avatar 
                src={drawingAuthor.avatarUrl} 
                size="xs" 
                radius="sm"
              />
            )}
            <Text size="sm" c="dimmed" ta="center">
              {drawingAuthor?.name || 'Someone'} drew this
            </Text>
          </Group>
        </MantineStack>
      )}
      
      {/* Drawing Display */}
      <Box style={{ width: `${imageSize}px`, height: `${imageSize}px`, maxWidth: '100%' }}>
        <Image 
          src={drawingUrl} 
          alt="Drawing to guess"
          radius="sm"
          style={{ 
            border: '1px solid var(--mantine-color-gray-3)',
            backgroundColor: '#ffffff',
            width: '100%',
            height: '100%',
            objectFit: 'contain'
          }}
        />
      </Box>
    </MantineStack>
  );

  // Controls (input form)
  const controls = (
    <Box w="100%" maw={isMobile ? '100%' : 400}>
      <form onSubmit={handleSubmit}>
        <MantineStack gap="md">
          <TextInput
            label={isMobile ? "" : "Your guess"}
            placeholder="What the heck is this?"
            value={guess}
            onChange={(e) => setGuess(e.currentTarget.value)}
            disabled={isSubmitting}
            required
            size={isMobile ? "md" : "lg"}
            styles={{
              input: {
                fontFamily: `"${handwritingFont}", var(--mantine-font-family)`,
                fontSize: '20px',
              }
            }}
          />

          {error && (
            <Text c="red" size="sm" ta="center">
              {error}
            </Text>
          )}

          <Button 
            type="submit" 
            size={isMobile ? "md" : "lg"}
            disabled={isSubmitting || !guess.trim()}
            loading={isSubmitting}
            fullWidth={isMobile}
            style={{ backgroundColor: '#0da388', color: 'white' }}
          >
            {isSubmitting ? 'Passing...' : 'Pass it!'}
          </Button>
        </MantineStack>
      </form>
    </Box>
  );

  return (
    <GameLayout
      mode="write"
      header={header}
      content={content}
      controls={controls}
    />
  );
};

export default GuessDrawing; 
